<template>
    <div class="edverum-table-orm-paginator">
        <div class="paginator-left">
            <span class="paginator-text tw:dark:!text-white">
                Showing: {{ firstRecord }} to {{ lastRecord }} of {{ totalRecords }} records
            </span>

            <select
                v-model="selectedPerPage"
                class="per-page-select"
                @blur="handlePerPageChange"
            >
                <option :value="10">10 per page</option>
                <option :value="25">25 per page</option>
                <option :value="50">50 per page</option>
                <option :value="100">100 per page</option>
            </select>

            <span v-if="disclaimerText" class="disclaimer-text">
                <e-icon icon="exclamation-triangle" class="disclaimer-icon" />
                {{ disclaimerText }}
            </span>
        </div>

        <div class="paginator-right">
            <span class="paginator-text tw:dark:!text-white">Go to page:</span>
            <input
                v-model.number="selectedPage"
                type="number"
                class="page-input"
                :min="1"
                :max="totalPages"
                @change="handlePageSelect"
                @keyup.enter="handlePageSelect"
            />

            <div class="page-buttons">
                <template v-for="(page, index) in pageButtons" :key="index">
                    <span v-if="page === '...'" class="page-ellipsis">...</span>
                    <button
                        v-else
                        class="page-btn"
                        :class="{ active: page === currentPage }"
                        @click="$emit('page-change', page)"
                    >
                        {{ page }}
                    </button>
                </template>
            </div>

            <button
                class="paginator-btn"
                :disabled="currentPage === 1 || totalRecords === 0"
                @click="$emit('page-change', currentPage - 1)"
            >
                <e-icon icon="angle-left" />
            </button>

            <button
                class="paginator-btn"
                :disabled="currentPage === totalPages || totalRecords === 0"
                @click="$emit('page-change', currentPage + 1)"
            >
                <e-icon icon="angle-right" />
            </button>
        </div>
    </div>
</template>

<script>
export default {
    name: 'EdverumTableOrmPaginator',
    props: {
        currentPage: {
            type: Number,
            required: true,
        },
        totalPages: {
            type: Number,
            required: true,
        },
        perPage: {
            type: Number,
            default: 25,
        },
        totalRecords: {
            type: Number,
            required: true,
        },
        disclaimerText: {
            type: String,
            default: '',
        },
    },
    emits: ['page-change', 'per-page-change'],
    data() {
        return {
            selectedPerPage: this.perPage,
            selectedPage: this.currentPage,
        };
    },
    computed: {
        firstRecord() {
            if (this.totalRecords === 0) return 0;
            return (this.currentPage - 1) * this.perPage + 1;
        },
        lastRecord() {
            const last = this.currentPage * this.perPage;
            return Math.min(last, this.totalRecords);
        },
        pageButtons() {
            const pages = [];
            const current = this.currentPage;
            const total = this.totalPages;

            if (total <= 1) return pages;

            // Add page 1 and ellipsis if we're far from the beginning
            const firstInRange = current === 1 ? 1 : current - 1;
            if (firstInRange > 2) {
                pages.push(1);
                pages.push('...');
            }

            // If on page 1, show pages 1, 2, (3 if exists)
            if (current === 1) {
                pages.push(1);
                if (total >= 2) pages.push(2);
                if (total >= 3) pages.push(3);
            } else {
                // Show previous, current, and next page
                if (current > 1) pages.push(current - 1);
                pages.push(current);
                if (current < total) pages.push(current + 1);
            }

            // Add ellipsis and last page if needed
            const lastInPages = pages[pages.length - 1];
            if (lastInPages < total - 1) {
                pages.push('...');
            }
            if (lastInPages < total) {
                pages.push(total);
            }

            return pages;
        },
    },
    watch: {
        currentPage(newVal) {
            this.selectedPage = newVal;
        },
        perPage(newVal) {
            this.selectedPerPage = newVal;
        },
    },
    methods: {
        handlePerPageChange() {
            this.$emit('per-page-change', this.selectedPerPage);
        },
        handlePageSelect() {
            const validPage = Math.max(1, Math.min(this.selectedPage || 1, this.totalPages));
            this.selectedPage = validPage;
            this.$emit('page-change', validPage);
        },
    },
};
</script>

<style lang="scss" scoped>
.edverum-table-orm-paginator {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    align-items: center;
    gap: 0.75rem;
    padding: 1rem;
    width: 100%;
    border-top: 1px solid var(--border);
}

.paginator-left {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.5rem;
}

.paginator-right {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.5rem;
}

.paginator-text {
    color: var(--text, var(--p-text-color, inherit));
    font-weight: 700;
    white-space: nowrap;
}

.per-page-select {
    height: 2.5rem;
    padding: 0 2rem 0 0.75rem;
    border: 1px solid var(--border);
    border-radius: 0.375rem;
    background-color: var(--scheme-main, #fff);
    color: var(--text, var(--p-text-color, inherit));
    font-weight: 300;
    cursor: pointer;
    appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23333' d='M6 9L1 4h10z'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 0.75rem center;
    background-size: 12px;
    min-width: 130px;

    &:focus {
        outline: none;
        border-color: #3b82f6;
    }
}

.disclaimer-text {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: #9ca3af;
    font-size: 0.875rem;
    font-weight: 300;
    white-space: nowrap;
}

.disclaimer-icon {
    color: #f59e0b;
}

.page-input {
    height: 2.5rem;
    width: 80px;
    padding: 0 0.75rem;
    border: 1px solid var(--border);
    border-radius: 0.375rem;
    background-color: var(--scheme-main, #fff);
    color: var(--text, var(--p-text-color, inherit));
    font-weight: 300;
    text-align: center;

    &:focus {
        outline: none;
        border-color: #3b82f6;
    }

    /* Remove spinner arrows in Chrome, Safari, Edge, Opera */
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }

    /* Remove spinner arrows in Firefox */
    &[type='number'] {
        -moz-appearance: textfield;
    }
}

.page-buttons {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.25rem;
}

.page-btn {
    height: 2.5rem;
    min-width: 2.5rem;
    padding: 0 0.75rem;
    border: 1px solid var(--border);
    border-radius: 0.375rem;
    background-color: var(--scheme-main, #fff);
    color: var(--text, var(--p-text-color, inherit));
    font-weight: 300;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
        background-color: var(--scheme-main-ter, #f7fafc);
    }

    &.active {
        background-color: #3b82f6;
        color: white;
        border-color: #3b82f6;
        font-weight: 500;
    }
}

.page-ellipsis {
    padding: 0 0.5rem;
    color: var(--text, var(--p-text-color, inherit));
    font-weight: 300;
}

.paginator-btn {
    height: 2.5rem;
    width: 2.5rem;
    border: 1px solid var(--border);
    border-radius: 0.375rem;
    background-color: var(--scheme-main, #fff);
    color: var(--text, var(--p-text-color, inherit));
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover:not(:disabled) {
        background-color: var(--scheme-main-ter, #f7fafc);
    }

    &:disabled {
        cursor: not-allowed;
        opacity: 0.4;
    }
}
</style>
